import { EditorType } from '@wix/ambassador-metasite-search-v2-search-entry/types';
import type { Article } from '@wix/answers-api';
import { ArticleSearchResultItem } from '@wix/cc-user-facing-common-components';
import type { ArticleData, HomepageContent } from '@wix/wix-help-widget-common/homepage';

import { ACTIONS, UI } from '../../../constants';
import { Attachment, TLocalesKeys } from '../../../types';
import { AttachmentErrorBiData } from '../../bi';
import { GenerateAnswerResult } from '../api/generateAnswer';
import { CategoryItemType } from '../api/getCategories';
import { SearchArticlesItem } from '../api/searchArticles';
import {
  IAIArticleAnswer,
  IArticle,
  IArticleSelector,
  IBotMessage,
  IBotTitleMessage,
  IBotTyping,
  IButtons,
  ICaraEntryPoint,
  ICategory,
  IChatTransferred,
  IConnectingAgent,
  IConversationFeedback,
  IDomainSelector,
  IGeneratedAnswerMessage,
  IImage,
  IInvoiceSelector,
  ILiveChatAgentStatus,
  ILiveChatChangeMode,
  ILiveChatMessage,
  ILoginSelector,
  IPhone,
  ISiteSelector,
  ISkillSuccessMessage,
  IUserAccountsSelector,
  IUserMessage,
  IVideo,
  IWaitingForAgent,
  IWhitelabelSelector,
} from '../messageCreators';
import { StartChatParams } from '../thunks/chatbotStartChat.thunk';

interface GenericMessageItem {
  clientOnly?: boolean;
}

export type TItem = GenericMessageItem &
  (
    | IArticle
    | IAIArticleAnswer
    | IBotMessage
    | IBotTitleMessage
    | IBotTyping
    | IUserMessage
    | IButtons
    | IPhone
    | IVideo
    | IImage
    | IGeneratedAnswerMessage
    | ICategory
    | ISiteSelector
    | IDomainSelector
    | IArticleSelector
    | IInvoiceSelector
    | ILoginSelector
    | IUserAccountsSelector
    | IConnectingAgent
    | IWhitelabelSelector
    | ISkillSuccessMessage
    | IConversationFeedback
    | ICaraEntryPoint
    // Live Chat
    | ILiveChatMessage
    | ILiveChatChangeMode
    | ILiveChatAgentStatus
    | IChatTransferred
    | IWaitingForAgent
  );

export interface IUserAction {
  type: UI;
  detail: {
    messageId: string;
    changeId?: string;
    [key: string]: unknown;
  };
}

export type IArticleState = {
  readonly article: Article | null;
  readonly isLoading: boolean;
  readonly error: ErrorStatus | null;
  readonly articleHeadingVisible: boolean;
  readonly isArticleHidden: boolean;
};

export interface IUndelivered {
  readonly [key: string]: IUserAction;
}

export enum ChatScrollToPositions {
  Bottom,
}

export enum ResetReason {
  StartOver,
  InternalMessage,
}

export type ChatScrollTo = {
  position: ChatScrollToPositions;
};

export type RestartChatParameters = Readonly<{
  nodeId?: string;
  referral?: string;
}>;

export interface IChatState {
  readonly isLocked: boolean;
  readonly isLiveChat: boolean;
  readonly isMuted: boolean;
  readonly isConnectingAgent?: boolean;
  readonly isDocumentVisible?: boolean;
  readonly conversationId?: string;
  readonly items: TItem[];
  readonly undelivered: IUndelivered;
  readonly unreadCount: number;
  readonly scrollTo?: ChatScrollTo;
  readonly startChatLoading?: boolean;
  readonly reloadStartChat?: boolean;
  readonly startChatError?: string;
  readonly startChatParameters?: StartChatParams;
  readonly restartChatParameters?: RestartChatParameters;
  readonly isReset?: boolean;
  readonly resetReason?: ResetReason;
  readonly startingNodeId?: string;
  // store Article data for each ArticlePreview message(by articleId)
  readonly articlePreviews?: Record<
    string /* articleId*/,
    Omit<IArticleState, 'articleHeadingVisible' | 'isArticleHidden'>
  >;
  readonly isFirstPriorityUser?: boolean;
  readonly itemsIdsOnLastPageLoad: string[];
  readonly isFromAnswerGeneration?: boolean;
  readonly isStartOverFromAnswerGeneration?: boolean;
  readonly pendingFollowUpQuestion?: string;
}

export enum AttachmentErrorType {
  Network,
  FilesLimit,
  FileSize,
  FileType,
}

export interface IGeneralError {
  localKey: TLocalesKeys;
  severity?: 'danger' | 'warning' | 'success';
  bi?: AttachmentErrorBiData;
  isClosable?: boolean;
  appendToTop?: boolean;
  retryData?: {
    messageId: string;
    messageText: string | undefined;
  };
}

export interface IAttachmentError extends IGeneralError {
  fileNames?: string[];
  learnMoreLink?: {
    localKey: TLocalesKeys;
    href: string;
  };
}

export interface IAttachesState {
  readonly attachments: Attachment[];
  readonly isLoading: boolean;
  readonly loadingFilesCount?: number;
}

export interface IErrorsState {
  readonly errors: IGeneralError[] | IAttachmentError[];
}

export interface IFieldState {
  readonly maxLength: number;
  readonly disabled: boolean;
  readonly hidden: boolean;
  readonly placeholder: string | undefined;
  readonly value: string;
  readonly attachFile: boolean;
  readonly multiline: boolean;
  readonly nodeId: string | undefined;
  readonly change: boolean | undefined;
  readonly contactFlow?: boolean;
  readonly prevState?: {
    value: string;
    attachFile: boolean;
    multiline: boolean;
    change: boolean | undefined;
    disabled: boolean;
  };
}

export enum PresentedState {
  SearchResults = 'Search Results',
  SingleArticle = 'Single Article',
  Chatbot = 'Chatbot',
  AllHelpTopics = 'All Help Topics',
  SolutionList = 'Solution List',
  Homepage = 'Homepage',
  WidgetButton = 'Widget Button',
}

export enum ErrorStatus {
  BadRequest = 400,
  NotFound = 404,
  ServerError = 500,
}

export interface IHelpCenterState {
  readonly article: IArticleState;
  readonly currentCategory: CategoryItemType | null;
  readonly categories?: CategoryItemType[];
  readonly categoryArticles: Article[];
  readonly normalizedCategories: Record<CategoryItemType['id'], CategoryItemType>;
  readonly fullPageSidebarOpened: boolean;
  readonly fullPageSidebarExtended: boolean;
  readonly searchedText: string;
  readonly searchSessionId: string | undefined;
  readonly searchInputType: string | undefined;
  readonly isSearchFocus: boolean;
  readonly currentPresentedState: PresentedState | undefined;
  readonly previousPresentedState: PresentedState | undefined;
  readonly hasHomeError: boolean;
}

export interface ISolutionsState {
  readonly query: string;
  readonly solutions: TSolution[] | null;
  readonly error: string | null;
  readonly isLoading: boolean;
  readonly isUserTypedSearch: boolean;
}

export interface IGenerateAnswerState extends GenerateAnswerResult {
  readonly error: string | null;
  readonly isLoading: boolean;
}

export interface IHomepageSectionState<T> {
  data: null | T;
  loading: boolean;
  error: string | null;
}

export interface ISearchConfig {
  hideSearch?: boolean;
  filters?: {
    categoriesIds?: string[];
    excludedCategoriesIds?: string[];
    editorType?: EditorType;
  };
}

export interface IHomepageState {
  content: IHomepageSectionState<HomepageContent>;
  articles: IHomepageSectionState<{
    articles: ArticleData[];
    modelId?: string;
  }>;
  reportABugFlowStarted: boolean;
  showReportABugConfirmationModal: boolean;
  reportABugPosition: number;
  reportABugText?: string;
  searchConfig: ISearchConfig;
}

export interface IBiEngagementState {
  mouseClick: boolean;
  mouseMove: boolean;
  scroll: boolean;
  scrollToBottom: boolean;
  scrollAddedContent: boolean;
  maxScrollAtFirstPosition: boolean;
  scrollPercentage: number; // percentage 0-100
}

// Modal actions
export interface IModalOpenAction {
  type: ACTIONS.MODAL_OPEN;
}

export interface IModalCloseAction {
  type: ACTIONS.MODAL_CLOSE;
}

export type TModalActions = IModalOpenAction | IModalCloseAction;

// meta
export type DealerOfferParams = Map<string, string>;

export interface IMetaState {
  readonly loadPageUrl: string;
  readonly startUrl: string;
  readonly origPlatform: string;
  readonly origPageName: string;
  readonly mainOptionName?: string;
  readonly intentId?: string;
  readonly dealerOfferParams?: DealerOfferParams;
  readonly referral?: string;
  readonly isCopilotInitialized?: boolean;
  readonly isCopilotChatbotVisible?: boolean;
  readonly suggestedQuestions?: string[];
}

export interface IMetaUpdatePageUrl {
  readonly type: ACTIONS.META_UPDATE_PAGE_URL;
  readonly loadPageUrl: string;
  readonly startUrl: string;
  readonly referral?: string;
  readonly isCopilotInitialized?: boolean;
}

export interface IMetaUpdateOrigParams {
  readonly type: ACTIONS.META_UPDATE_ORIG_PARAMS;
  readonly origPlatform: string;
  readonly origPageName: string;
}

export interface IMetaUpdateIntentId {
  readonly type: ACTIONS.META_UPDATE_INTENT_ID;
  readonly intentId: string;
  readonly mainOptionName: string;
}

export interface IMetaUpdateDealerOfferParams {
  readonly type: ACTIONS.META_UPDATE_DEALER_OFFER_PARAMS;
  readonly dealerOfferParams: DealerOfferParams;
}

export interface IMetaUpdateArticleUri {
  readonly type: ACTIONS.META_UPDATE_ARTICLE_URI;
  readonly articleUri: string;
}

export interface IMetaUpdateCopilotChatbotVisible {
  readonly type: ACTIONS.META_UPDATE_COPILOT_CHATBOT_VISIBLE;
  readonly isCopilotChatbotVisible: boolean;
}

export interface IMetaUpdateSuggestedQuestions {
  readonly type: ACTIONS.META_UPDATE_SUGGESTED_QUESTIONS;
  readonly suggestedQuestions: string[];
}

export type TMetaActions =
  | IMetaUpdatePageUrl
  | IMetaUpdateIntentId
  | IMetaUpdateOrigParams
  | IMetaUpdateDealerOfferParams
  | IMetaUpdateArticleUri
  | IMetaUpdateCopilotChatbotVisible
  | IMetaUpdateSuggestedQuestions;

export type TSolution = SearchArticlesItem | ArticleSearchResultItem;

export type TActions = TModalActions | TMetaActions;

export interface ILastClickedLink {
  url: string;
  text: string;
  pageType?: string;
}
